/* src/data/menu.js
   Single source of truth for MenuPage + ItemPage
   ✅ Compatible with src/pages/menu/ItemPage.jsx
   Mockup: 2 pizzas only, deeper editorial copy, English.
*/

export const THEME = {
  blue: "#0087E6",
  navy: "#0F172A",
  sky: "#F0F9FF",
  white: "#FFFFFF",
  gold: "#FFD700",
};

export const D10 = {
  website: "https://www.d10s.pizza",
  menu: "https://www.d10s.pizza/menu",
  instagram: "https://www.instagram.com/d10spizza/",
  facebook: "https://www.facebook.com/d10spizza/",
  addressLine: "Prosika 51A, Pag, Croatia",
  phoneDisplay: "+385 95 501 1065",
  email: "d10spizzafantasista@gmail.com",
  googleMaps:
    "https://www.google.com/maps/search/?api=1&query=Prosika%2051A%2C%20Pag%2C%20Croatia",
};

export const SECTIONS = [
  {
    key: "pizze",
    title: "Our Champions",
    description:
      "Neapolitan discipline with a D10 attitude. Every pizza gets a profile, not a paragraph.",
    image:
      "https://images.unsplash.com/photo-1590947132387-155cc02f3212?q=80&w=1800&auto=format&fit=crop",
  },
  {
    key: "fritta",
    title: "Fritta Fantasista",
    description:
      "A different rhythm. Fried, crisp, and built for the beach crowd. (Not in this mockup yet.)",
    image:
      "https://images.unsplash.com/photo-1601924582970-9238bcb495d9?q=80&w=1800&auto=format&fit=crop",
  },
];

/* ---- Helpers ---- */
const EUR = (n) => {
  const v = Number(n);
  if (Number.isNaN(v)) return `${n}`;
  return `${v.toFixed(2).replace(".", ",")} €`;
};

/* ---- PIZZAS (2 only) ----
   Fields used by ItemPage.jsx:
   - id, slug, name, nickname, price, heroImage
   - description (TheDish)
   - ingredients[] (TheSquad)
   - pairing (TheAssist)
   - playerInspiration, playerBio, quote.text (TheLegacy)
*/
export const PIZZAS = [
 {
  id: 1,
  slug: "buffons-marinara",
  section: "pizze",
  style: "Rossa (No Cheese)",
  name: "Buffon’s Marinara",
  nickname: "The Clean Sheet",
  price: EUR(9.5),

  shortDescription:
    "Tomato, garlic, oregano and olive oil — nothing to hide, nothing to forgive. A pizza for people who notice details.",

  tags: ["Vegetarian", "Classic", "Minimal"],

  jerseyNumber: "1",
  jerseyColor: "#94a3b8",

  badgeLabel: "Icon Series",
  bakeLabel: "Pure. Focused. Unforgiving.",

  ticker: [
    { type: "anton", text: "Clean Sheet Pizza" },
    { type: "serif", text: "★ For people who notice details" },
    { type: "antonDark", text: "No Mistakes Allowed" },
  ],

  bgWords: ["FOCUS", "CONTROL", "CLARITY"],

  philosophy: {
    kicker: "The Idea",
    lines: ["Nothing to", "hide behind.", "Only execution."],
  },

  kitchenQuote:
    "If something is off, you feel it immediately. That’s not a bug — that’s the point.",

  sensory: {
    texture: "Light & dry",
    profile: "Sharp & clean",
    finish: "Precise",
  },

  description:
    "This pizza is built like a great goalkeeper. Not loud. Not flashy. No tricks, no distractions — just positioning, timing and consistency.\n\nWith no cheese to smooth over mistakes, every element is exposed: the sweetness of the tomato, the edge of garlic, the dryness of oregano, the weight of olive oil. When it works, it feels effortless. When it doesn’t, you notice instantly.\n\nThat’s why this pizza isn’t comforting — it’s satisfying. It rewards attention. It’s for people who enjoy when things are done right.",

  ingredients: [
    {
      name: "San Marzano Tomatoes",
      role: "The Base",
      desc: "Bright, slightly sweet and clean — the backbone of the entire experience.",
    },
    {
      name: "Oregano",
      role: "The Structure",
      desc: "Defines the aroma and keeps the flavour disciplined and dry.",
    },
    {
      name: "Garlic",
      role: "The Edge",
      desc: "Adds tension and contrast. Sharp at first, warm after.",
    },
    {
      name: "Extra Virgin Olive Oil",
      role: "The Flow",
      desc: "Carries aroma, connects flavours and adds weight without heaviness.",
    },
    {
      name: "Fresh Basil",
      role: "The Accent",
      desc: "A final green note that keeps everything lifted and fresh.",
    },
  ],

  pairing:
    "Cold lager, mineral water with lemon, or anything that refreshes rather than competes",

  heroImage: "/img/buffon-hero.jpg",
  detailImage: "/img/buffon.jpg",
  ogImage: "/img/og/buffons-marinara.png",

  playerInspiration: "Gianluigi Buffon",
  playerBio:
    "Buffon built a legendary career on fundamentals. Positioning over spectacle. Calm over chaos. He made the difficult look simple — and the simple feel reliable. That same philosophy defines this pizza.",

  quote: {
    text: "The hardest things look easy when done properly.",
    author: "Buffon",
  },

  meta: {
    allergens: ["Gluten"],
    note:
      "No cheese by design. If you enjoy clean flavours, structure and contrast — this is your pizza.",
  },
},
{
  id: 5,
  slug: "cafus-margherita",
  section: "pizze",
  style: "Rossa",
  name: "Cafu’s Margherita",
  nickname: "The Endless Run",
  price: EUR(10.5),

  jerseyNumber: "2",
  jerseyColor: "#DC2626",
  jerseyTextColor: "#FFFFFF",
  jerseyName: "CAFU",

  shortDescription:
    "Tomato, mozzarella, olive oil and basil — the pizza that quietly tells you if everything is done right.",

  tags: ["Vegetarian", "Classic", "Benchmark"],

  badgeLabel: "Icon Series",
  bakeLabel: "Balance over brilliance",

  ticker: [
    { type: "anton", text: "The Reference Pizza" },
    { type: "serif", text: "★ The one you measure everything against" },
    { type: "antonDark", text: "Never stops working" },
  ],

  bgWords: ["RHYTHM", "BALANCE", "FLOW"],

  philosophy: {
    kicker: "The Idea",
    lines: ["Nothing steals", "the spotlight.", "Everything plays."],
  },

  kitchenQuote:
    "If one element tries too hard, the whole thing falls apart.",

  sensory: {
    texture: "Soft & elastic",
    profile: "Fresh & milky",
    finish: "Clean",
  },

  description:
    "This pizza is built on rhythm.\n\nTomato, mozzarella, olive oil and basil in constant movement — nothing static, nothing loud, nothing trying to impress on its own. When it works, you don’t notice the parts. You notice the flow.\n\nThe tomato brings lift. The mozzarella adds softness. Olive oil carries everything forward. Basil arrives as a final signal that resets your palate for the next bite. The experience never peaks — it continues.\n\nThat’s what makes this pizza special. It doesn’t aim for drama. It aims for reliability. You can eat it fast or slow, early or late, hungry or not — and it still works. That’s not simplicity. That’s control.",

  ingredients: [
    {
      name: "San Marzano Tomatoes",
      role: "The Spark",
      desc:
        "Bright, lightly sweet and never heavy. Keeps the pizza fresh instead of filling.",
    },
    {
      name: "Fior di Latte",
      role: "The Balance",
      desc:
        "Soft, milky mozzarella that melts without overpowering or turning greasy.",
    },
    {
      name: "Extra Virgin Olive Oil",
      role: "The Flow",
      desc:
        "Connects sauce and cheese into one continuous texture and aroma.",
    },
    {
      name: "Fresh Basil",
      role: "The Accent",
      desc:
        "Aromatic lift that keeps the finish clean and light.",
    },
  ],

  pairing:
    "Crisp lager, a citrus-leaning IPA, or a dry mineral white wine",

  heroImage:
    "/img/cafu-hero.jpg",
  detailImage: "/img/cafu.jpg",
  ogImage: "/img/og/cafus-margherita.png",

  playerInspiration: "Cafu",
  playerBio:
    "Cafu wasn’t about moments — he was about presence. Always available. Always contributing. Always in motion. His game was built on stamina, reliability and timing rather than spectacle. That’s what this pizza represents: not a highlight, but the standard everything else has to live up to.",

  quote: {
    text: "Consistency is the real luxury.",
    author: "Cafu",
  },

  meta: {
    allergens: ["Gluten", "Dairy"],
    note:
      "Vegetarian. The best choice if you want to understand how everything here is done.",
  },
},

  {
  id: 3,
  slug: "maldinis-amatriciana",
  section: "pizze",
  style: "Gialla (Roman / Savory)",
  name: "Maldini’s Amatriciana",
  nickname: "The Perfect Line",
  price: EUR(13.5),

  jerseyNumber: "3",
  jerseyColor: "#c3c6cdff",
  jerseyTextColor: "#FFFFFF",
  jerseyName: "MALDINI",

  shortDescription:
    "Yellow tomato, fior di latte, Amatriciana sauce and crisp guanciale — Roman character, perfectly positioned.",

  tags: ["Savory", "Roman", "Refined"],

  badgeLabel: "Icon Series",
  bakeLabel: "Perfect positioning",

  ticker: [
    { type: "anton", text: "The Perfect Line" },
    { type: "serif", text: "★ Roman character, Milanese control" },
    { type: "antonDark", text: "Nothing out of position" },
  ],

  bgWords: ["ELEGANCE", "STRUCTURE", "CONTROL"],

  philosophy: {
    kicker: "The Idea",
    lines: ["Strong foundation.", "Perfect positioning.", "Clean execution."],
  },

  kitchenQuote:
    "Elegance is knowing exactly where everything belongs.",

  sensory: {
    texture: "Creamy & crisp",
    profile: "Savory & balanced",
    finish: "Clean with a salty edge",
  },

  description:
    "This pizza is built around positioning.\n\nA smooth base of yellow tomato brings sweetness, brightness and a softer acidity. Fior di latte settles into it with calm, creamy balance, creating a foundation that feels rich without becoming heavy.\n\nThen the Amatriciana sauce introduces its Roman character — deep, savory and confidently direct. Crisp guanciale adds salt, texture and intensity, but never takes control of the entire pizza.\n\nEvery ingredient has a role. Every flavour holds its line. Nothing is added for decoration, and nothing is left out of position.\n\nStrong enough to make an impression. Refined enough to make it look effortless.",

  ingredients: [
    {
      name: "Yellow Tomato Base",
      role: "The Foundation",
      desc:
        "Sweet, vibrant and gently acidic, creating a smooth base for the richer ingredients.",
    },
    {
      name: "Fior di Latte",
      role: "The Balance",
      desc:
        "Creamy mozzarella that softens the intensity and keeps the pizza composed.",
    },
    {
      name: "Amatriciana Sauce",
      role: "The Structure",
      desc:
        "A rich Roman-inspired sauce that brings tomato depth, savoury character and controlled intensity.",
    },
    {
      name: "Guanciale",
      role: "The Intervention",
      desc:
        "Crisp, deeply savoury cured pork cheek that arrives at exactly the right moment.",
    },
  ],

  pairing:
    "Dry Italian white wine, light Sangiovese, crisp lager, or sparkling water with lemon",

  heroImage:
    "/img/maldini-hero.jpg",
  detailImage: "/img/maldini.jpg",
  ogImage: "/img/og/maldinis-amatriciana.png",

  playerInspiration: "Paolo Maldini",
  playerBio:
    "Maldini made defending look effortless because his positioning was almost always perfect. He never needed unnecessary movement or aggression. This pizza follows the same logic: a strong yellow tomato foundation, carefully balanced richness and guanciale used with precision rather than excess.",

  quote: {
    text: "Class is knowing where to be before the moment arrives.",
    author: "Maldini",
  },

  meta: {
    allergens: ["Gluten", "Dairy"],
    note:
      "Contains pork. Savoury and rich, balanced by the sweetness and gentle acidity of yellow tomato.",
  },
},

{
  id: 4,
  slug: "guardiolas-cacio-e-pepe",
  section: "pizze",
  style: "Bianca",
  name: "Guardiola’s Cacio e Pepe",
  nickname: "The System",
  price: EUR(13.5),

  jerseyNumber: "4",
  jerseyColor: "#0F172A",
  jerseyTextColor: "#FFFFFF",
  jerseyName: "GUARDIOLA",

  shortDescription:
    "A layered white pizza where every element has a role — nothing shines alone, everything works together.",

  tags: ["Vegetarian", "Complex", "Layered"],

  badgeLabel: "Icon Series",
  bakeLabel: "Structure over spontaneity",

  ticker: [
    { type: "anton", text: "The System Pizza" },
    { type: "serif", text: "★ Built, not improvised" },
    { type: "antonDark", text: "Every role matters" },
  ],

  bgWords: ["STRUCTURE", "RELATIONSHIPS", "CONTROL"],

  philosophy: {
    kicker: "The Idea",
    lines: ["No hero.", "Only roles.", "Only system."],
  },

  kitchenQuote:
    "If you remove one part, the whole thing collapses.",

  sensory: {
    texture: "Layered & elastic",
    profile: "Rich & sharp",
    finish: "Long",
  },

  description:
    "This pizza isn’t built around a star ingredient. It’s built around relationships.\n\nRicotta creates the base layer of softness. Fior di latte adds structure and stretch. Gorgonzola brings depth and intensity. Provola introduces a subtle smoky line. Paški sir anchors everything with sharpness and identity.\n\nPepper cuts through the richness and keeps the system dynamic. Basil resets the palate so the next bite feels as precise as the first.\n\nTaken alone, each element is interesting. Together, they become coherent. This pizza doesn’t ask to be noticed — it asks to be understood.",

  ingredients: [
    {
      name: "Ricotta",
      role: "The Base",
      desc:
        "Soft and light. Prevents the blend from becoming heavy or flat.",
    },
    {
      name: "Fior di Latte",
      role: "The Structure",
      desc:
        "Provides melt, stretch and the familiar foundation of a white pizza.",
    },
    {
      name: "Gorgonzola",
      role: "The Depth",
      desc:
        "Adds intensity and a long, savoury finish.",
    },
    {
      name: "Provola",
      role: "The Smoke",
      desc:
        "Introduces subtle smokiness and extra melt.",
    },
    {
      name: "Paški Sir",
      role: "The Identity",
      desc:
        "Sharp, salty and unmistakable — the anchor that gives character.",
    },
    {
      name: "Mixed Pepper",
      role: "The Tension",
      desc:
        "Cuts through richness and keeps the profile alive and dynamic.",
    },
    {
      name: "Fresh Basil",
      role: "The Reset",
      desc:
        "Aromatic freshness that cleans the finish and prepares the next bite.",
    },
  ],

  pairing:
    "Dry sparkling wine, a sharp pilsner, or anything crisp that refreshes the palate",

  heroImage:
    "/img/guardiola-hero.jpg",
  detailImage: "/img/guardiola.jpg",
  ogImage: "/img/og/guardiolas-cacio-e-pepe.png",

  playerInspiration: "Pep Guardiola",
  playerBio:
    "Guardiola’s football isn’t about individuals — it’s about structure, positioning and relationships. Each player exists to enable the next. This pizza follows the same logic: no ingredient is the star, but each is essential.",

  quote: {
    text: "Details are not details. They are everything.",
    author: "Guardiola",
  },

  meta: {
    allergens: ["Gluten", "Dairy"],
    note:
      "Vegetarian. Rich and layered — best enjoyed slowly, with attention.",
  },
},
{
  id: 6,
  slug: "beckenbauers-classic",
  section: "pizze",
  style: "Rossa",
  name: "Beckenbauer’s Classic",
  nickname: "The Emperor",
  price: EUR(13.5),

  jerseyNumber: "5",
  jerseyColor: "#111827",
  jerseyTextColor: "#FFFFFF",
  jerseyName: "BECKENBAUER",

  shortDescription:
    "Tomato, mozzarella, ham, mushrooms and oregano — the definition of what a classic is supposed to feel like.",

  tags: ["Classic", "Timeless"],

  badgeLabel: "Icon Series",
  bakeLabel: "Timeless authority",

  ticker: [
    { type: "anton", text: "The Classic Pizza" },
    { type: "serif", text: "★ The original benchmark" },
    { type: "antonDark", text: "Nothing to prove" },
  ],

  bgWords: ["AUTHORITY", "STRUCTURE", "TIMELESS"],

  philosophy: {
    kicker: "The Idea",
    lines: ["No trends.", "No noise.", "Only standard."],
  },

  kitchenQuote:
    "Some things don’t evolve. They endure.",

  sensory: {
    texture: "Soft & familiar",
    profile: "Warm & savory",
    finish: "Comforting",
  },

  description:
    "This pizza is not a throwback — it’s a reference.\n\nTomato, mozzarella, ham, mushrooms and oregano in exactly the proportions people have loved for decades. Not because it’s nostalgic, but because it works.\n\nThe tomato stays bright. The mozzarella keeps things soft and balanced. The ham adds gentle salt and richness. Mushrooms bring warmth and depth. Oregano locks it into that unmistakable pizzeria aroma.\n\nIt’s not here to surprise you. It’s here to reassure you. This is what a proper pizza feels like.",

  ingredients: [
    {
      name: "San Marzano Tomatoes",
      role: "The Base",
      desc:
        "Bright, lightly sweet and clean — the foundation everything else relies on.",
    },
    {
      name: "Fior di Latte",
      role: "The Balance",
      desc:
        "Soft, milky mozzarella that supports rather than dominates.",
    },
    {
      name: "Prosciutto Cotto",
      role: "The Body",
      desc:
        "Mild, salty richness that gives the pizza depth and comfort.",
    },
    {
      name: "Mushrooms",
      role: "The Earth",
      desc:
        "Adds warmth, texture and gentle savoury notes.",
    },
    {
      name: "Oregano",
      role: "The Signature",
      desc:
        "Defines the aroma and locks the pizza into its classic identity.",
    },
  ],

  pairing:
    "A classic lager, an amber ale, or a light, easy red wine",

  heroImage:
    "/img/beckenbauer-hero.jpg",
  detailImage: "/img/beckenbauer.jpg",
  ogImage: "/img/og/beckenbauers-classic.png",

  playerInspiration: "Franz Beckenbauer",
  playerBio:
    "Beckenbauer wasn’t just great — he defined what greatness should look like. Calm authority, structure and intelligence. This pizza carries the same energy: composed, dependable and always in control.",

  quote: {
    text: "True classics never need reinvention.",
    author: "Beckenbauer",
  },

  meta: {
    allergens: ["Gluten", "Dairy"],
    note:
      "Contains meat (ham). The reference point if you want to understand our idea of a classic pizza.",
  },
},
{
  id: 2,
  slug: "roberto-carlos-freekick",
  section: "pizze",
  style: "Gialla (Smoky / Spicy)",
  name: "Roberto Carlos’s Freekick",
  nickname: "The Cannon",

  jerseyNumber: "6",
  jerseyColor: "#ffd900ff",
  jerseyTextColor: "#FFFFFF",
  jerseyName: "ROBERTO CARLOS",

  price: EUR(13.5),

  shortDescription:
    "Yellow base, smoked provola, grilled eggplant and ’nduja — bright at first sight, explosive underneath.",

  tags: ["Spicy", "Smoky", "Bold"],

  badgeLabel: "Icon Series",
  bakeLabel: "Explosive by nature",

  ticker: [
    { type: "anton", text: "The Yellow Cannon" },
    { type: "serif", text: "★ Smoke, heat and unexpected power" },
    { type: "antonDark", text: "Built to strike" },
  ],

  bgWords: ["POWER", "SPEED", "FIRE"],

  philosophy: {
    kicker: "The Idea",
    lines: ["Bright entrance.", "Smoky buildup.", "Explosive finish."],
  },

  kitchenQuote:
    "Some shots bend. This one burns.",

  sensory: {
    texture: "Creamy & charred",
    profile: "Smoky & spicy",
    finish: "Warm and lingering",
  },

  description:
    "Bright yellow on the surface. Serious power underneath.\n\nThe yellow base opens the pizza with a smooth, vibrant character before smoked provola brings depth, richness and a long, unmistakable aroma.\n\nGrilled eggplant adds a soft, charred layer that absorbs everything around it, while ’nduja cuts through with heat, intensity and controlled chaos. A final sprinkle of young onion brings freshness and a sharp green lift to every bite.\n\nIt starts smooth, bends unexpectedly and finishes with fire — just like a Roberto Carlos free kick.",

  ingredients: [
    {
      name: "Yellow Base",
      role: "The Jersey",
      desc:
        "A smooth, vibrant foundation inspired by the unmistakable yellow of Brazil.",
    },
    {
      name: "Smoked Provola",
      role: "The Power",
      desc:
        "Deep, creamy and smoky cheese that gives the pizza its strength.",
    },
    {
      name: "Grilled Eggplant",
      role: "The Control",
      desc:
        "Soft, lightly charred slices that carry the smoke and balance the heat.",
    },
    {
      name: "’Nduja",
      role: "The Cannon",
      desc:
        "Spicy, spreadable Calabrian sausage that strikes with sudden intensity.",
    },
    {
      name: "Young Onion",
      role: "The Final Touch",
      desc:
        "A fresh, crisp sprinkle that lifts the richness and sharpens the finish.",
    },
  ],

  pairing:
    "Crisp lager, hoppy pale ale, sparkling wine, or a chilled light red",

  heroImage:
    "/img/roberto-carlos-hero.jpg",
  detailImage: "/img/roberto-carlos.jpg",
  ogImage: "/img/og/roberto-carloss-giallo-cannon.png",

  playerInspiration: "Roberto Carlos",
  playerBio:
    "Roberto Carlos made power feel unpredictable. Speed, confidence and impossible movement arriving from an angle no one could prepare for. This pizza carries the same energy: bright and inviting at first, then smoky, spicy and explosive once it begins to open up.",

  quote: {
    text: "Some shots bend. This one burns.",
    author: "Roberto Carlos",
  },

  meta: {
    allergens: ["Gluten", "Dairy"],
    note:
      "Contains pork. Smoky, rich and moderately spicy.",
  },
},



{
  id: 8,
  slug: "beckhams-diavola",
  section: "pizze",
  style: "Rossa (Spicy)",
  name: "Beckham’s Diavola",
  nickname: "The Icon",
  price: EUR(14.6),

  jerseyNumber: "7",
  jerseyColor: "#DC2626",
  jerseyTextColor: "#FFFFFF",
  jerseyName: "BECKHAM",

  shortDescription:
    "Spice, cream and confidence — a bold pizza with a polished finish.",

  tags: ["Spicy", "Bold", "Iconic"],

  badgeLabel: "Icon Series",
  bakeLabel: "Heat with finesse",

  ticker: [
    { type: "anton", text: "The Icon Pizza" },
    { type: "serif", text: "★ Bold, polished, unforgettable" },
    { type: "antonDark", text: "Fire with control" },
  ],

  bgWords: ["CONTRAST", "GLAMOUR", "PRECISION"],

  philosophy: {
    kicker: "The Idea",
    lines: ["Bold outside.", "Precise inside.", "Always controlled."],
  },

  kitchenQuote:
    "Real impact isn’t chaos — it’s control.",

  sensory: {
    texture: "Creamy & sharp",
    profile: "Spicy & smooth",
    finish: "Clean",
  },

  description:
    "This pizza is built on contrast.\n\nSpice against cream. Boldness against polish. ’Nduja spreads deep, warming heat. Smoked salami adds structure and bite. Then stracciatella arrives to soften everything into something smooth and indulgent.\n\nTomato keeps the base bright and disciplined so the heat never becomes muddy. Mozzarella adds body and melt. Basil finishes fresh.\n\nIt’s not messy heat. It’s tailored heat.",

  ingredients: [
    {
      name: "San Marzano Tomatoes",
      role: "The Base",
      desc:
        "Bright and clean — keeps the spice focused, not overwhelming.",
    },
    {
      name: "Fior di Latte Mozzarella",
      role: "The Balance",
      desc:
        "Carries the heat without amplifying it.",
    },
    {
      name: "Stracciatella",
      role: "The Soften",
      desc:
        "Creamy contrast that cools the spice and adds luxury.",
    },
    {
      name: "’Nduja",
      role: "The Fire",
      desc:
        "Spicy Calabrian salami that brings deep, warming heat.",
    },
    {
      name: "Smoked Spicy Salami",
      role: "The Edge",
      desc:
        "Firm, smoky spice that adds structure and bite.",
    },
    {
      name: "Fresh Basil",
      role: "The Finish",
      desc:
        "Aromatic freshness that cleans the finish.",
    },
  ],

  pairing:
    "Cold pilsner, light IPA, or anything crisp and refreshing",

  heroImage:
    "/img/beckham-hero.jpg",
  detailImage: "/img/beckham.jpg",
  ogImage: "/img/og/beckhams-diavola.png",

  playerInspiration: "David Beckham",
  playerBio:
    "Beckham’s game was about precision wrapped in style. Every pass measured. Every moment composed. This pizza follows the same logic: bold and confident on the surface, disciplined and controlled underneath.",

  quote: {
    text: "Make it bold, but make it beautiful.",
    author: "Beckham",
  },

  meta: {
    allergens: ["Gluten", "Dairy"],
    note:
      "Contains spicy meat. Medium–hot, but balanced.",
  },
},{
  id: 7,
  slug: "prosikitos-mortadella",
  section: "pizze",
  style: "Bianca",
  name: "Prosikito’s Mortadella",
  nickname: "The Crowd Pleaser",
  price: EUR(14.8),

  jerseyNumber: "8",
  jerseyColor: "#DC2626",
  jerseyTextColor: "#FFFFFF",
  jerseyName: "PROSIKITO",

  shortDescription:
    "Creamy, nutty, silky and joyful — the pizza that makes people smile before they even finish the first slice.",

  tags: ["Indulgent", "Playful"],

  badgeLabel: "Icon Series",
  bakeLabel: "Pure joy",

  ticker: [
    { type: "anton", text: "The Joy Pizza" },
    { type: "serif", text: "★ Built for smiles" },
    { type: "antonDark", text: "Impossible not to love" },
  ],

  bgWords: ["JOY", "COMFORT", "PLEASURE"],

  philosophy: {
    kicker: "The Idea",
    lines: ["Not about tactics.", "Not about rules.", "About happiness."],
  },

  kitchenQuote:
    "Sometimes the best move is simply to make people happy.",

  sensory: {
    texture: "Silky & soft",
    profile: "Rich & nutty",
    finish: "Playful",
  },

  description:
    "This pizza is built on joy, not strategy.\n\nA soft white base of ricotta and mozzarella sets the tone. Mortadella adds gentle salt and richness without heaviness. Stracciatella pushes it into full indulgence. Pistachio pesto and crunch then cut through with nuttiness, aroma and texture.\n\nIt’s rich, but never tiring. Creamy, but never flat. Every bite feels a little bit fun.\n\nThe name comes from a story. When Prosinečki played in Spain, people struggled with his name — so he became ‘Prosikito’. And Prosika is the beach right next to us. This pizza connects all of that into one thing: local, joyful, and a little playful.",

  ingredients: [
    {
      name: "Ricotta",
      role: "The Base",
      desc:
        "Light, creamy foundation that keeps the pizza soft and airy.",
    },
    {
      name: "Fior di Latte Mozzarella",
      role: "The Structure",
      desc:
        "Adds melt and body without making things heavy.",
    },
    {
      name: "Mortadella",
      role: "The Heart",
      desc:
        "Mild, aromatic cured meat with gentle spice and richness.",
    },
    {
      name: "Stracciatella",
      role: "The Luxury",
      desc:
        "Silky creaminess that melts into every bite.",
    },
    {
      name: "Pistachio Pesto & Crunch",
      role: "The Contrast",
      desc:
        "Nutty, aromatic and textured — keeps the richness lively.",
    },
  ],

  pairing:
    "Dry prosecco, a crisp pilsner, or a citrus-forward spritz",

  heroImage:
    "/img/prosikito-hero.jpg",
  detailImage: "/img/prosikito.jpg",
  ogImage: "/img/og/prosikitos-mortadella.png",

  playerInspiration: "Robert Prosinečki",
  playerBio:
    "Prosinečki’s game was about joy. About flair, confidence and doing things with a smile. He made football feel playful again. This pizza follows that same spirit — indulgent, comforting and impossible not to enjoy.",

  quote: {
    text: "If it makes people smile, it’s already winning.",
    author: "Prosinečki",
  },

  meta: {
    allergens: ["Gluten", "Dairy", "Nuts"],
    note:
      "Contains nuts (pistachio) and dairy. Rich, creamy and indulgent.",
  },
},
{
  id: 11,
  slug: "ronaldos-marine-predator",
  section: "pizze",
  style: "Rossa",
  name: "Ronaldo’s Marine Predator",
  nickname: "The Finisher",
  price: EUR(14.6),

  jerseyNumber: "9",
  jerseyColor: "#111827",
  jerseyTextColor: "#FFFFFF",
  jerseyName: "RONALDO",

  shortDescription:
    "Tuna, red onion, chili and double mozzarella — a direct, high-impact pizza that doesn’t miss.",

  tags: ["Bold", "Savory", "High Impact"],

  badgeLabel: "Icon Series",
  bakeLabel: "One touch. Done.",

  ticker: [
    { type: "anton", text: "The Finisher Pizza" },
    { type: "serif", text: "★ Direct, fast, decisive" },
    { type: "antonDark", text: "No second chances" },
  ],

  bgWords: ["DECISIVE", "POWER", "FINISH"],

  philosophy: {
    kicker: "The Idea",
    lines: ["Arrives fast.", "Hits clean.", "Leaves nothing behind."],
  },

  kitchenQuote:
    "Some flavours don’t build slowly. They strike.",

  sensory: {
    texture: "Juicy & structured",
    profile: "Salty & sharp",
    finish: "Spiky",
  },

  description:
    "This pizza is unapologetically direct.\n\nTuna brings deep salty umami. Red onion adds sharp crunch. Chili brings tension. Oregano locks it into that familiar, classic pizzeria energy.\n\nThe cheese setup is the key: fior di latte gives melt and balance, while buffalo mozzarella adds richness and depth. Together they soften the edges just enough — so the bite stays powerful, but never harsh.\n\nNo slow warm-up. No hidden meaning. It hits, it finishes, and it’s gone.",

  ingredients: [
    {
      name: "San Marzano Tomatoes",
      role: "The Base",
      desc:
        "Bright tomato that keeps tuna and onion focused, not heavy.",
    },
    {
      name: "Fior di Latte Mozzarella",
      role: "The Melt",
      desc:
        "Classic stretch and balance — keeps the bite coherent.",
    },
    {
      name: "Mozzarella di Bufala",
      role: "The Richness",
      desc:
        "Creaminess that rounds the sharp elements and extends the finish.",
    },
    {
      name: "Tuna",
      role: "The Power",
      desc:
        "Deep, salty umami — the core hit of the whole profile.",
    },
    {
      name: "Red Onion",
      role: "The Bite",
      desc:
        "Fresh sharpness and crunch that cuts through richness.",
    },
    {
      name: "Chili Peppers",
      role: "The Heat",
      desc:
        "Adds fire and tension without taking over the flavour.",
    },
    {
      name: "Oregano",
      role: "The Lock",
      desc:
        "Ties everything together into that unmistakable classic aroma.",
    },
  ],

  pairing:
    "Cold lager, dry cider, or a sharp citrus spritz",

  heroImage:
    "/img/ronaldo-hero.jpg",
  detailImage: "/img/ronaldo.jpg",
  ogImage: "/img/og/ronaldos-marine-predator.png",

  playerInspiration: "Ronaldo",
  playerBio:
    "Ronaldo is decisiveness. He appears at the exact moment you switch off — and the game is over. No hesitation, no doubt, no wasted movement. This pizza plays the same way: clear intent, strong flavour, immediate impact.",

  quote: {
    text: "When the moment comes, you take it.",
    author: "Ronaldo",
  },

  meta: {
    allergens: ["Gluten", "Dairy", "Fish"],
    note:
      "Contains fish (tuna). Bold, savory, and built for people who like their pizza with attitude.",
  },
},
{
  id: 9,
  slug: "dalma",
  section: "pizze",
  style: "Bianca (Olive Oil Base)",
  name: "Dalma",
  nickname: "The Local Legend",
  price: EUR(14.5),

  jerseyNumber: "10",
  jerseyColor: "#0EA5E9",
  jerseyTextColor: "#FFFFFF",
  jerseyName: "DALMA",

  shortDescription:
    "Olive oil base, arugula, Dalmatian prosciutto, cherry tomatoes and Paški sir — a fresh slice of Pag with a #10 story behind it.",

  tags: ["Local", "Fresh", "Signature"],

  badgeLabel: "Icon Series",
  bakeLabel: "Pag, but polished",

  ticker: [
    { type: "anton", text: "The Local Legend" },
    { type: "serif", text: "★ Dalmatia meets Pag" },
    { type: "antonDark", text: "A #10 kind of move" },
  ],

  bgWords: ["LOCAL", "LIFT", "IDENTITY"],

  philosophy: {
    kicker: "The Idea",
    lines: ["Light base.", "Coastal energy.", "Finishes sharp."],
  },

  kitchenQuote:
    "The best classics feel inevitable — like they were always meant to exist.",

  sensory: {
    texture: "Light & crisp",
    profile: "Fresh & savory",
    finish: "Sharp",
  },

  description:
    "This pizza is D10 speaking the local language — confidently, not loudly.\n\nIt starts with olive oil instead of tomato because the goal here is lift, not weight. Arugula brings peppery freshness. Cherry tomatoes add sweet brightness. Dalmatian prosciutto gives that salty backbone that feels coastal and clean.\n\nThen Paški sir changes the tempo. Dry, sharp, unmistakably Pag. It doesn’t melt into the background — it defines the finish.\n\nThe name has a story too. Maradona’s daughter is called Dalma. Dalmatia is the coast we’re part of. And Pag is where you’re eating this. So Dalma becomes a local legend with a #10 reference — simple, emotional, and easy to remember.",

  ingredients: [
    {
      name: "Olive Oil Base",
      role: "The Foundation",
      desc:
        "Keeps everything clean and light. Lets the toppings speak without fighting a heavy sauce.",
    },
    {
      name: "Arugula",
      role: "The Lift",
      desc:
        "Peppery freshness that adds height and keeps the bite airy.",
    },
    {
      name: "Dalmatian Prosciutto",
      role: "The Backbone",
      desc:
        "Salty, cured depth that feels rich without turning heavy.",
    },
    {
      name: "Cherry Tomatoes",
      role: "The Spark",
      desc:
        "Sweet brightness that keeps the profile clean and lively.",
    },
    {
      name: "Paški Sir",
      role: "The Identity",
      desc:
        "Sharp, salty and unmistakably local — the ingredient that makes it Pag.",
    },
  ],

  pairing:
    "Pošip (white wine), a crisp pilsner, or a tonic with citrus",

  heroImage:
    "/img/dalma-hero.jpg",
  detailImage: "/img/dalma.jpg",
  ogImage: "/img/og/dalma.png",

  playerInspiration: "Diego Maradona",
  playerBio:
    "Maradona was pure feeling. He turned football into emotion — not just performance. Dalma carries that same spirit in a local way: simple ingredients, high personality, and a finish you remember. It’s not trying to be complicated. It’s trying to be true.",

  quote: {
    text: "Genius doesn’t shout. It just feels right.",
    author: "Maradona",
  },

  meta: {
    allergens: ["Gluten", "Dairy"],
    note:
      "Contains meat (prosciutto) and Paški sir. Fresh, local, and very ‘Pag’.",
  },
},{
  id: 10,
  slug: "mo-salahs-bresaola",
  section: "pizze",
  style: "Rossa",
  name: "Mo Salah’s Bresaola",
  nickname: "The Counter Attack",
  price: EUR(14.5),

  jerseyNumber: "11",
  jerseyColor: "#DC2626",
  jerseyTextColor: "#FFFFFF",
  jerseyName: "SALAH",

  shortDescription:
    "Lean cured beef, fresh greens and bright tomato — fast, clean and instantly effective.",

  tags: ["Fresh", "Lean", "High Pace"],

  badgeLabel: "Icon Series",
  bakeLabel: "Speed with precision",

  ticker: [
    { type: "anton", text: "The Counter Pizza" },
    { type: "serif", text: "★ Light, fast, lethal" },
    { type: "antonDark", text: "Arrives when you least expect it" },
  ],

  bgWords: ["SPEED", "PRECISION", "IMPACT"],

  philosophy: {
    kicker: "The Idea",
    lines: ["Built light.", "Moves fast.", "Finishes clean."],
  },

  kitchenQuote:
    "The best moves feel sudden and inevitable at the same time.",

  sensory: {
    texture: "Light & layered",
    profile: "Fresh & savory",
    finish: "Clean",
  },

  description:
    "This pizza is built for speed.\n\nA bright tomato base sets the rhythm. Classic mozzarella gives melt and structure. Then everything shifts.\n\nLean bresaola arrives cold and clean. Arugula adds peppery lift. Yellow cherry tomatoes bring sweetness and freshness that keep the whole thing light instead of heavy.\n\nIt’s not greasy. It’s not slow. It’s fast, sharp and controlled — a pizza that feels light but leaves a strong impression.",

  ingredients: [
    {
      name: "San Marzano Tomatoes",
      role: "The Launch",
      desc:
        "Bright tomato base that keeps the profile clean and fast.",
    },
    {
      name: "Fior di Latte Mozzarella",
      role: "The Melt",
      desc:
        "Classic stretch and balance without weight.",
    },
    {
      name: "Mozzarella di Bufala",
      role: "The Richness",
      desc:
        "Adds creaminess and depth to support the lean cured beef.",
    },
    {
      name: "Bresaola",
      role: "The Strike",
      desc:
        "Lean, cured beef with clean salt and depth — arrives late and changes the bite.",
    },
    {
      name: "Arugula",
      role: "The Lift",
      desc:
        "Peppery freshness that keeps everything light and sharp.",
    },
    {
      name: "Yellow Cherry Tomatoes",
      role: "The Sweet Cut",
      desc:
        "Bright sweetness that balances salt and greens.",
    },
  ],

  pairing:
    "Dry lager, crisp rosé, or sparkling water with citrus",

  heroImage:
    "/img/salah-hero.jpg",
  detailImage: "/img/salah.jpg",
  ogImage: "/img/og/mo-salahs-bresaola.png",

  playerInspiration: "Mohamed Salah",
  playerBio:
    "Salah is what happens when timing meets speed. One moment it’s calm — the next it’s over. This pizza plays the same way: classic base, then a fresh, fast finish that hits immediately.",

  quote: {
    text: "Stay light. Move fast. Finish clean.",
    author: "Salah",
  },

  meta: {
    allergens: ["Gluten", "Dairy"],
    note:
      "Contains meat (bresaola). Fresh, lean and fast-finishing.",
  },
},
{
  id: 11,
  slug: "fritta-fantasista-margherita",
  section: "fritta",
  style: "Fried (Air Fryer)",
  name: "Fritta Fantasista Margherita",
  nickname: "The Crunch Proof",
  price: EUR(10.5),

  shortDescription:
    "San Marzano DOP, ricotta, fior di latte, smoked provola, Paški sir crunch. Fried for crispness.",

  tags: ["Vegetarian"],

  description:
    "This is Margherita, but in a different rhythm. Same familiar anchors — tomato, cheese, balance — but finished with a fried, crisp shell that turns the whole bite into something louder and more snackable.\n\nRicotta keeps it soft inside, fior di latte gives that classic melt, and smoked provola adds a warm, smoky depth that fits the fried texture perfectly. Then Paški sir crunch comes on top like a final seasoning layer — salty, sharp, and addictive. Crisp outside, soft inside, and gone faster than you planned.",

  ingredients: [
    {
      name: "San Marzano DOP",
      role: "The Base",
      desc:
        "Bright tomato foundation that stays fresh even with a crisp, fried finish.",
    },
    {
      name: "Ricotta",
      role: "The Soft Core",
      desc:
        "Keeps the inside creamy and light, not dense.",
    },
    {
      name: "Fior di Latte",
      role: "The Melt",
      desc:
        "Classic mozzarella texture that makes it feel familiar.",
    },
    {
      name: "Smoked Provola",
      role: "The Depth",
      desc:
        "Adds smokiness that pairs perfectly with crispness.",
    },
    {
      name: "Paški Sir Crunch",
      role: "The Finish",
      desc:
        "Salty, sharp crunch that turns the last bite into the best bite.",
    },
  ],

  pairing: "Cold lager, sparkling citrus drink, or a crisp spritz",

  heroImage:
    "https://images.unsplash.com/photo-1601924582970-9238bcb495d9?q=80&w=2200&auto=format&fit=crop",
  detailImage:
      "/img/salah.jpg",

  playerInspiration: "The Street Game",
  playerBio:
    "Fritta is pizza that doesn’t sit still. Crisp, playful, and built to be eaten with momentum. Same fundamentals, different finish — the kind of move you remember.",
  quote: {
    text: "Same soul. Different texture. More chaos.",
  },

  meta: {
    allergens: ["Gluten", "Dairy"],
    note: "Vegetarian. Fried finish = crisp outside, soft inside.",
  },
},

{
  id: 12,
  slug: "fritta-fantasista-il-fenomeno",
  section: "fritta",
  style: "Fried (Air Fryer)",
  name: "Fritta Fantasista Il Fenomeno",
  nickname: "The Chaos Finisher",
  price: EUR(10.5),

  shortDescription:
    "San Marzano DOP, ricotta, fior di latte, onion, kulen, Paški sir, fried for crispness. Loud and addictive.",

  tags: ["Spicy"],

  description:
    "This one is built to be loud. Tomato brings brightness, ricotta keeps the inside soft, fior di latte melts into comfort — then onion adds sweetness and crunch, and kulen brings smoky heat.\n\nThe fried finish makes it snackable and aggressive in the best way: crisp shell, soft center, spicy edges. Paški sir on top adds sharp salt that cuts through everything and keeps the bite clean. It’s messy in concept, but precise in execution. A proper phenomenon.",

  ingredients: [
    {
      name: "San Marzano DOP",
      role: "The Base",
      desc:
        "Bright tomato that keeps the profile clean even with spice and fried texture.",
    },
    {
      name: "Ricotta",
      role: "The Soft Core",
      desc:
        "Creamy inside that balances the heat and crunch.",
    },
    {
      name: "Fior di Latte",
      role: "The Melt",
      desc:
        "Classic mozzarella body that makes it satisfying, not just spicy.",
    },
    {
      name: "Onion",
      role: "The Sweet Cut",
      desc:
        "Adds sweetness and bite to counter the kulen’s heat.",
    },
    {
      name: "Kulen",
      role: "The Fire",
      desc:
        "Smoky, spicy kick with real backbone.",
    },
    {
      name: "Paški Sir",
      role: "The Sharp Finish",
      desc:
        "Salty, sharp, and local — it tightens the whole flavor profile.",
    },
  ],

  pairing: "Cold lager, light IPA, or anything crisp and refreshing",

  heroImage:
    "https://images.unsplash.com/photo-1513104890138-7c749659a591?q=80&w=2200&auto=format&fit=crop",
  detailImage:
    "https://images.unsplash.com/photo-1601924582970-9238bcb495d9?q=80&w=1600&auto=format&fit=crop",

  playerInspiration: "R9 Energy",
  playerBio:
    "Some things are pure finesse. This isn’t that. This is speed, heat, and chaos, but still controlled. Crisp outside, soft inside, spicy edges, and a finish that makes you go back in.",
  quote: {
    text: "If it’s too polite, it’s not Il Fenomeno.",
  },

  meta: {
    allergens: ["Gluten", "Dairy"],
    note: "Contains spicy meat (kulen). Fried finish. Addictive.",
  },
}






];

/* ✅ Critical for ItemPage.jsx ScoutReport */
D10.pizzas = PIZZAS;

/* ✅ Used by ItemPage.jsx */
export const getPizzaBySlug = (slug) => PIZZAS.find((p) => p.slug === slug);

/* Optional helpers (for MenuPage) */
export const getPizzasBySection = (sectionKey) =>
  PIZZAS.filter((p) => (p.section || "pizze") === sectionKey).sort(
    (a, b) => (a.id ?? 0) - (b.id ?? 0)
  );

export const getSectionsFromData = () => {
  const keysInData = Array.from(new Set(PIZZAS.map((p) => p.section || "pizze")));
  const preferredOrder = SECTIONS.map((s) => s.key);

  keysInData.sort((a, b) => {
    const ia = preferredOrder.indexOf(a);
    const ib = preferredOrder.indexOf(b);
    if (ia === -1 && ib === -1) return a.localeCompare(b);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });

  return keysInData;
};
